import { faBan, faCheck, faCommentDots, faEdit, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import React, { useEffect, useState, useRef } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import BsButton from '../../collection/bootstrap/BsButton';
import DataTable from '../../collection/DataTable';
import TableHandler from '../../collection/TableHandler';
import ValidacionFilter from '../../components/filters/ValidacionFilter';
import VacacionesModal from '../../components/modals/VacacionesModal';
import VacacionesComentarioResponsableModal from '../../components/modals/VacacionesComentarioResponsableModal';
import FloatingAddButton from '../../components/project/FloatingAddButton';
import Validado from '../../components/project/Validado';
import { apiDelete, apiForm, apiGet, getSession, apiBlobForm } from '../../utils/api';
import { generateFormData, sortableString } from '../../utils/functions';
import { swalert, swalertConfirm } from '../../utils/notifications';

export default function Vacaciones(props) {
    // default filter
    const usuario = getSession();
    const defaultFilter = {
        desde: moment().startOf('year').format("YYYY-MM-DD"),
        hasta: null,
        trabajador: props.super ? null : usuario.id,
        pendiente: props.super ? true : null,
        validado: props.super ? null : true
    };
    const filter = useRef(generateFormData(defaultFilter));

    // show excel button
    const [showExcelButton, setShowExcelButton] = useState(false);

    // api calls
    const updateData = async newFilter => {
        const actualFilter = newFilter ?? filter.current;
        filter.current = actualFilter;
        newFilter ? setShowExcelButton(true) : setShowExcelButton(false);
        return await apiForm("vacaciones/table", actualFilter);
    }
    const deleteRow = async id => await apiDelete("vacaciones/delete/" + id);
    const performAction = async (action, id) => await apiGet(`vacaciones/${action}/${id}`);
    const downloadExcel = async () => {
        const blob = await apiBlobForm("vacaciones/excel", filter.current);
        const url = window.URL.createObjectURL(blob);

        // download file
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'Vacaciones_' + filter.current.get("desde") + '_' + filter.current.get("hasta") + '.xlsx');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    // render
    return <>
        <h2>{`${props.super ? "Validar" : "Solicitar"} vacaciones`}</h2>
        <TableHandler
            onUpdate={updateData}
            onDelete={deleteRow}
            onAction={performAction}
            elementText="las vacaciones">
            <ValidacionFilter
                className="my-3" fem
                defaultValue={defaultFilter}
                selectFechas
                selectDepartamento={props.super && usuario.permisoAdmin}
                selectTrabajador={props.super}
                selectEstado />
            {(usuario.permisoAdmin || usuario.permisoValidacion) && showExcelButton && <BsButton className="mb-2" onClick={downloadExcel}>Descargar Excel</BsButton>}
            <VacacionesTable super={props.super} user={usuario} />
        </TableHandler>
    </>;
}

function VacacionesTable(props) {
    const [dias, setDias] = useState();
    const [sendingRequest, setSendingRequest] = useState(false);

    useEffect(() => { updateData(); }, [props]);
    const updateData = async () => setDias(await apiGet("vacaciones/getdias"));

    const handleRequestValidation = async () => {
        const ok = await swalertConfirm(
            "¿Solicitar validación?",
            "Se notificará a tu responsable por email para que valide las vacaciones pendientes.");
        if (ok) {
            setSendingRequest(true);
            if (await apiGet("vacaciones/requestvalidation")) {
                swalert("Ok", "Solicitud enviada", "success");
            }
            setSendingRequest(false);
        }
    }

    // table columns
    const columns = [
        {
            name: "Trabajador",
            selector: row => sortableString(row.NombreTrabajador),
            format: row => row.NombreTrabajador,
            sortable: true,
            omit: !props.super
        },
        {
            name: "Año",
            selector: row => row.Ano + moment(row.FechaInicio).format("YYYY-MM-DD"),
            format: row => row.Ano,
            sortable: true,
            right: true
        },
        {
            name: "Total / Restantes",
            selector: row => {
                const d = row.DiasTrabajador;
                return `${d.total} / ${d.restantes + d.pendientes}`;
            },
            omit: !props.super,
            right: true
        },
        {
            name: "Num. días",
            selector: row => row.NumDias,
            sortable: true,
            right: true
        },
        {
            name: "Inicio",
            selector: row => moment(row.FechaInicio).format("YYYY-MM-DD"),
            format: row => moment(row.FechaInicio).format("DD/MM/YY"),
            sortable: true
        },
        {
            name: "Fin",
            selector: row => moment(row.FechaFin).format("YYYY-MM-DD"),
            format: row => moment(row.FechaFin).format("DD/MM/YY"),
            sortable: true
        },
        {
            name: "Comentario",
            selector: row => sortableString(row.Comentario),
            format: row => row.Comentario,
            sortable: true
        },
        {
            name: "Comentario resp.",
            selector: row => sortableString(row.ComentarioResponsable),
            format: row => row.ComentarioResponsable,
            sortable: true
        },
        {
            name: "Validación",
            selector: row => row.Pendiente ? 0 : row.Validado ? 1 : 2,
            format: row => <Validado pendiente={row.Pendiente} validado={row.Validado} sufix="a" size="lg" />,
            sortable: true,
            center: true
        }
    ];
    // table buttons
    const buttons = row => {
        return <>{
            props.super ?
                (row.Pendiente ?
                    <>
                        {/* super && pendiente */}
                        <Button title="Comentar" variant="info" size="sm" onClick={props.onEdit}><FontAwesomeIcon icon={faCommentDots} /></Button>
                        <Button title="Aprobar" action="validate" variant="success" size="sm" onClick={props.onAction}><FontAwesomeIcon icon={faCheck} /></Button>
                        <Button title="Denegar" action="invalidate" variant="danger" size="sm" onClick={props.onAction}><FontAwesomeIcon icon={faTimes} /></Button>
                    </> :
                    (row.Validado ? <>
                        {/* super && !pendiente && validado */}
                        <Button title="Cancelar" action="cancel" variant="outline-danger" size="sm" onClick={props.onAction}><FontAwesomeIcon icon={faBan} /></Button>
                    </> : "")
                ) :
                (row.Pendiente ?
                    <>
                        {/* !super && pendiente */}
                        <Button variant="secondary" size="sm" onClick={props.onEdit}><FontAwesomeIcon icon={faEdit} /></Button>
                        <Button variant="danger" size="sm" onClick={props.onDelete}><FontAwesomeIcon icon={faTrash} /></Button>
                    </> : ""
                )
        }</>
    };
    const name = (row) => (props.super ? `${row.NombreTrabajador}, ` : "") + `${moment(row.FechaInicio).format("DD/MM/YY")}-${moment(row.FechaFin).format("DD/MM/YY")}`;

    // render
    return <>
        {(!props.super && dias) &&
            <Row className="display-flex align-items-center gy-3">
                <Col sm="4" xs="6">
                    Días totales: {dias.total} <br />
                    Restantes: {dias.restantes} <br />
                    {dias.acumulados != 0 && `Acumulados: ${dias.acumulados}`}
                </Col>
                <Col sm="4" xs="6">
                    Disfrutados: {dias.disfrutados} <br />
                    Aprobados: {dias.aprobados} <br />
                    Pendientes: {dias.pendientes} <br />
                    Días de baja descontados : {dias.diasBajaDescontados} de 5
                </Col>
                <Col sm="4" xs="12">
                    <BsButton variant="success" isLoading={sendingRequest} onClick={handleRequestValidation}>Solicitar validación</BsButton>
                </Col>
            </Row>
        }
        <DataTable responsive rowname={name} buttons={buttons} columns={columns} data={props.data} pagination />
        {props.formOpen &&
            (props.super && props.formId != 0 ?
                <VacacionesComentarioResponsableModal
                    show={props.formOpen}
                    onHide={props.onFormClose}
                    idVacaciones={props.formId} /> :
                <VacacionesModal
                    show={props.formOpen}
                    onHide={props.onFormClose}
                    idVacaciones={props.formId}
                    permisoAdmin={props.user.permisoAdmin}
                    super={props.super}
                />
            )
        }
        <FloatingAddButton onClick={props.onNew} />
    </>;
}
